import React, { useEffect, useRef, useState } from "react";
import ConditionalRender from "../../reusable-components/ConditionalRender";
import CustomTableWithPagination from "../../reusable-components/CustomTable";
import styles from "./CustomerDatabaseList.module.scss";
import {
  addCustomerFormSchema,
  archiveFormSchema,
  customerDatabaseColumns,
  CustomerSortColumns,
  filterDrawerSchema,
  NO_CUSTOMER_LIST_MESSAGE,
} from "./constants";
import Form from "../Form";
import UtilityBar from "./UtilityBar";
import FilterDrawer from "./FilterDrawer";
import AddCustomerForm from "./AddCustomerForm";
import { appRoutes } from "../../constants/appRoutes";
import { generatePath, useHistory } from "react-router-dom";
import CustomerDatabaseService from "../../services/customerDatabase.service";
import TNLoader from "../../reusable-components/TNLoader";
import ArchiveConfirmModal from "./ArchiveConfirmationModal";
import { useCxDatabaseContext } from "../../context/CustomerDatabaseFilterContext";
import { storageKeys } from "../../constants/storageKeys";
import JobDatabaseService from "../../services/jobDatabase.service";
import { ApiResponse } from "../../enums/ApiResponse";
import { TableSortTypes } from "../../enums/ViewListingTypes";
import { FILTER_FIELD_NAMES_TO_COUNT } from "./FilterDrawer/constants";
import { countFiltersApplied } from "../../utils/countAppliedFilters";

const CustomerDatabaseList = () => {
  const navigate = useHistory();
  const { cxDatabaseFilters, updateCxDatabaseFilters } = useCxDatabaseContext();
  const { GetPaginationDefaultMeta } = JobDatabaseService();
  const isMountRef = useRef<boolean>(true);
  const [pageSizeChanged, setPageSizeChanged] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(
    Number(sessionStorage?.getItem(storageKeys?.CUSTOMER_DB_PAGESIZE) ?? 10),
  );
  const [filterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false);
  const [addCustomerFormOpen, setAddCustomerFormOpen] =
    useState<boolean>(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<undefined | string>();
  const [isEditForm, setIsEditForm] = useState<boolean>(false);
  const [customerId, setCustomerId] = useState<undefined | string>();

  const { GetCustomerDatabaseEntries } = CustomerDatabaseService();
  const { data: defaultData, isFetching: gettingDefaults } =
    GetPaginationDefaultMeta();

  const {
    data,
    refetch,
    isPending,
    isFetching,
    isLoading,
    isRefetching,
    isSuccess: listFetchSuccess,
  } = GetCustomerDatabaseEntries(
    {
      page: currentPage,
      perPage: pageSize,
      sortBy: cxDatabaseFilters?.sortBy ?? CustomerSortColumns.COMPANY_NAME,
      sortOrder: cxDatabaseFilters?.sortOrder ?? TableSortTypes.ASCEND,
      latitude: cxDatabaseFilters?.latitude,
      longitude: cxDatabaseFilters?.longitude,
      radius: cxDatabaseFilters?.radius,
      search: searchValue,
      setDefaultPageSize: !(
        defaultData?.data?.customerDefaultPageSize === pageSize
      ),
    },
    pageSizeChanged,
    !!sessionStorage.getItem(storageKeys.CUSTOMER_DB_PAGESIZE),
  );

  const filterCount = countFiltersApplied(
    cxDatabaseFilters,
    FILTER_FIELD_NAMES_TO_COUNT,
  );

  const handleFilterDrawerOpen = () => setFilterDrawerOpen(true);
  const handleFilterDrawerClose = () => setFilterDrawerOpen(false);

  const handleArchiveConfirmOpen = (record?: any) => {
    setCustomerId(record?.id);
    setArchiveConfirmOpen(true);
  };
  const handleArchiveConfirmClose = () => {
    setCustomerId(undefined);
    setArchiveConfirmOpen(false);
  };

  const handleAddCustomerOpen = () => setAddCustomerFormOpen(true);
  const handleAddCustomerClose = () => {
    if (isEditForm) {
      setCustomerId(undefined);
      setIsEditForm(false);
    }
    setAddCustomerFormOpen(false);
  };

  const handleEditRecordAction = (record?: any) => {
    setIsEditForm(true);
    setCustomerId(record?.id);
    handleAddCustomerOpen();
  };

  const handleViewNavigate = (record?: any) => {
    const locationProp = {
      customerName: record?.companyName,
      navigatedFromQuote: false,
      isBlocked: false,
    };
    navigate.push(
      generatePath(appRoutes.CUSTOMER_PROFILE_ID, { id: record?.id }),
      locationProp,
    );
  };

  const handleCurrentPage = (page: number) => {
    setCurrentPage(page);
    sessionStorage.setItem(storageKeys.CUSTOMER_DB_PAGE, JSON.stringify(page));
  };

  const handlePaginationPageSize = (size: number) => {
    setPageSize(size);
    setPageSizeChanged(true);
    setCurrentPage(1);
    try {
      sessionStorage.setItem(storageKeys.CUSTOMER_DB_PAGESIZE, size);
    } catch (err) {
      console.error("Err", err);
    }
  };

  const handleSearchValue = (searchValue: string) => {
    setSearchValue(searchValue);
  };

  const handleSorterAction = (sorter) => {
    if (!!!sorter?.order) {
      updateCxDatabaseFilters({
        sortBy: undefined,
        sortOrder: undefined,
      });
      return;
    }
    updateCxDatabaseFilters({
      sortBy: sorter?.columnKey,
      sortOrder: sorter?.order,
    });
  };

  const handleSetFilters = (filterValues) => {
    updateCxDatabaseFilters({
      ...filterValues,
    });
    setCurrentPage(1);
  };

  const handleClearAllFilter = () => {
    updateCxDatabaseFilters({
      latitude: undefined,
      longitude: undefined,
      address: undefined,
      radius: undefined,
    });
    setCurrentPage(1);
  };

  useEffect(() => {
    const storageItem = sessionStorage.getItem(storageKeys.CUSTOMER_DB_PAGE);
    if (!!storageItem) {
      const pageValue = JSON.parse(storageItem);
      setCurrentPage(pageValue);
    }
    const storageItemSize = sessionStorage.getItem(
      storageKeys.CUSTOMER_DB_PAGESIZE,
    );
    if (!!storageItemSize) {
      const pageSizeValue = JSON.parse(storageItemSize);
      setPageSize(pageSizeValue);
    }
  }, []);

  useEffect(() => {
    if (data?.meta?.perPage !== pageSize && !!data?.meta?.perPage)
      setCurrentPage(1);
  }, [pageSize, data?.meta?.perPage]);

  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }
    handleCurrentPage(1);
  }, [
    cxDatabaseFilters?.address,
    cxDatabaseFilters?.latitude,
    cxDatabaseFilters?.longitude,
    cxDatabaseFilters?.radius,
    cxDatabaseFilters?.sortBy,
    cxDatabaseFilters?.sortOrder,
    searchValue,
  ]);

  useEffect(() => {
    if (
      !!defaultData?.message &&
      defaultData?.message === ApiResponse.SUCCESS
    ) {
      setPageSize(defaultData?.data?.customerDefaultPageSize);
    }
  }, [defaultData]);

  useEffect(() => {
    if (pageSizeChanged && listFetchSuccess) {
      setPageSizeChanged(false);
    }
  }, [pageSizeChanged, listFetchSuccess]);

  return (
    <>
      <Form validationSchema={filterDrawerSchema}>
        {() => (
          <>
            <UtilityBar
              handleFilterDrawerOpen={handleFilterDrawerOpen}
              handleAddCustomerOpen={handleAddCustomerOpen}
              handleSetSearchValue={handleSearchValue}
              filterCount={filterCount}
              handleClearAllFilter={handleClearAllFilter}
            />
            <FilterDrawer
              visible={filterDrawerOpen}
              handleClose={handleFilterDrawerClose}
              filterValues={cxDatabaseFilters}
              handleSetFilters={handleSetFilters}
              searchValue={searchValue}
              handleSetSearchValue={handleSearchValue}
              handleClearAllFilter={handleClearAllFilter}
            />
          </>
        )}
      </Form>
      <Form validationSchema={addCustomerFormSchema}>
        {() => (
          <AddCustomerForm
            isOpen={addCustomerFormOpen}
            handleClose={handleAddCustomerClose}
            isEdit={isEditForm}
            customerId={customerId}
            profileView={false}
          />
        )}
      </Form>
      <Form validationSchema={archiveFormSchema}>
        {() => (
          <ArchiveConfirmModal
            isOpen={archiveConfirmOpen}
            handleClose={handleArchiveConfirmClose}
            customerId={customerId}
          />
        )}
      </Form>
      <TNLoader
        isLoading={
          isFetching ||
          isPending ||
          isRefetching ||
          isLoading ||
          gettingDefaults
        }
      />
      <ConditionalRender
        visible={!!data?.data?.length}
        fallback={
          <div className={styles.emptyContainer}>
            <p>{NO_CUSTOMER_LIST_MESSAGE}</p>
          </div>
        }
      >
        <CustomTableWithPagination
          data={data?.data ?? []}
          columns={customerDatabaseColumns({
            handleArchiveAction: handleArchiveConfirmOpen,
            handleEditAction: handleEditRecordAction,
            filterValues: cxDatabaseFilters,
          })}
          isClickableTable
          handleSelectedRow={handleViewNavigate}
          totalData={data?.meta?.totalRecords}
          currentPage={currentPage}
          pageSize={pageSize}
          handleChange={handleCurrentPage}
          handleSizeChangerChange={handlePaginationPageSize}
          isSortable={true}
          handleSorter={handleSorterAction}
        />
      </ConditionalRender>
    </>
  );
};

export default CustomerDatabaseList;
